import { Country } from '@infuse/ui/domain';
import { defineStore } from 'pinia';
import de from 'world_countries_lists/data/countries/de/world.json';
import en from 'world_countries_lists/data/countries/en/world.json';
import es from 'world_countries_lists/data/countries/es/world.json';
import fr from 'world_countries_lists/data/countries/fr/world.json';
import it from 'world_countries_lists/data/countries/it/world.json';
import nl from 'world_countries_lists/data/countries/nl/world.json';

type CountryList = Readonly<Country[]>;

interface State {
  readonly countries: Map<string, CountryList>; // <intl locale, countries>
  locale: string; // intl locale
}

// [intl-locale, alpha2, alpha3]
const SUPPORTED_LOCALES = [
  ['de', 'DE', 'DEU'],
  ['en', 'GB', 'GBR'],
  ['es', 'ES', 'ESP'],
  ['fr', 'FR', 'FRA'],
  ['it', 'IT', 'ITA'],
  ['nl', 'NL', 'NLD']
];

const mapToUpperCase = (locale: CountryList): CountryList => {
  return locale.map((c: Country): Country => {
    return {
      id: c.id,
      alpha2: c.alpha2.toUpperCase(),
      alpha3: c.alpha3.toUpperCase(),
      name: c.name
    };
  });
};

export const useCountryStore = defineStore('country', {
  state: () => {
    return {
      countries: new Map<string, CountryList>([
        ['de', mapToUpperCase(de)],
        ['en', mapToUpperCase(en)],
        ['es', mapToUpperCase(es)],
        ['fr', mapToUpperCase(fr)],
        ['it', mapToUpperCase(it)],
        ['nl', mapToUpperCase(nl)]
      ]),
      locale: 'en'
    } as State;
  },
  getters: {
    getCountries(): CountryList {
      return this.countries.get(this.locale) ?? en; // fallback to english
    },

    getFrequentCountries(): CountryList {
      return this.getCountries.filter((c: Country) => SUPPORTED_LOCALES.map((sl) => sl[1]).includes(c.alpha2));
    },

    getNonFrequentCountries(): CountryList {
      return this.getCountries.filter((c: Country) => !SUPPORTED_LOCALES.map((sl) => sl[1]).includes(c.alpha2));
    }
  },
  actions: {
    // Get the country from the given locale if it is a supported one. English otherwise.
    findByAlpha3(intlLocale: string, alpha3?: string): Country | undefined {
      if (!alpha3) {
        return undefined;
      }

      if (SUPPORTED_LOCALES.map((sl) => sl[0]).includes(intlLocale)) {
        this.locale = intlLocale;
      } else {
        this.locale = 'en';
      }

      return this.getCountries.find((c: Country) => c.alpha3 === alpha3.toUpperCase());
    },

    supportedLocaleAsAlpha3(intlLocale: string): string | undefined {
      // Map the given locale to alpha3 code, but only if it is a supported locale.
      if (!intlLocale) {
        return undefined;
      }
      return SUPPORTED_LOCALES.find((sl: string[]) => sl[0] === intlLocale)?.[2];
    }
  }
});
